/* eslint-disable */
import { MEMORY_CONFIG } from '@/constants/memoryConfig'
import { ChocoboLazyLoader } from '@/services/chocoboLazyLoader'
import { RetirementManager } from '@/services/retirementManager'

export class MemoryManager {
  static memoryStats = {
    lastCheck: 0,
    estimatedUsage: 0,
    compressionLevel: 0,
    lastCleanup: 0
  }

  static compressionLevels = [
    {
      level: 0,
      name: 'None',
      threshold: 0,
      actions: []
    },
    {
      level: 1,
      name: 'Light',
      threshold: MEMORY_CONFIG.compressionThreshold,
      actions: [
        'compressInactiveChocobos',
        'trimTransactionHistory',
        'cleanupExpiredAuctions'
      ]
    },
    {
      level: 2,
      name: 'Moderate',
      threshold: MEMORY_CONFIG.memoryWarningThreshold,
      actions: [
        'compressInactiveChocobos',
        'trimTransactionHistory',
        'cleanupExpiredAuctions',
        'compressOldRaceHistory',
        'retireOldChocobos'
      ]
    },
    {
      level: 3,
      name: 'Aggressive',
      threshold: MEMORY_CONFIG.criticalThreshold,
      actions: [
        'compressInactiveChocobos',
        'trimTransactionHistory',
        'cleanupExpiredAuctions',
        'compressOldRaceHistory',
        'retireOldChocobos',
        'compressActiveChocobos',
        'clearCaches'
      ]
    }
  ]

  static checkMemoryUsage(gameStore) {
    const now = Date.now()
    
    // Only check every 30 seconds to avoid overhead
    if (now - this.memoryStats.lastCheck < 30000) {
      return this.memoryStats
    }

    // Estimate memory usage
    const estimated = this.estimateMemoryUsage(gameStore)
    this.memoryStats.estimatedUsage = estimated
    this.memoryStats.lastCheck = now

    // Determine required compression level
    const requiredLevel = this.determineCompressionLevel(estimated)
    
    if (requiredLevel > this.memoryStats.compressionLevel) {
      this.applyCompressionLevel(requiredLevel, gameStore)
    }

    return this.memoryStats
  }

  static estimateMemoryUsage(gameStore) {
    let totalBytes = 0

    // Estimate chocobo data
    totalBytes += gameStore.chocobos.length * this.getChocoboSize(gameStore.chocobos[0])

    // Estimate owner data
    totalBytes += gameStore.owners.length * 2000 // ~2KB per owner

    // Estimate jockey data
    totalBytes += gameStore.jockeys.length * 1000 // ~1KB per jockey

    // Estimate transaction history
    totalBytes += gameStore.recentTransactions.length * 500 // ~500B per transaction

    // Estimate auction data
    totalBytes += gameStore.activeAuctions.length * 3000 // ~3KB per auction

    // Add compression cache overhead
    const cacheSize = ChocoboLazyLoader.detailCache.size * 15000 // ~15KB per cached chocobo
    totalBytes += cacheSize

    return Math.round(totalBytes / 1024 / 1024 * 100) / 100 // Convert to MB
  }

  static getChocoboSize(sampleChocobo) {
    if (!sampleChocobo) return 5000 // Default estimate

    let size = 2000 // Base chocobo data

    // Race history contribution
    if (sampleChocobo.raceHistory) {
      size += sampleChocobo.raceHistory.length * 150 // ~150B per race
    }

    return size
  }

  static determineCompressionLevel(estimatedMB) {
    for (let i = this.compressionLevels.length - 1; i >= 0; i--) {
      if (estimatedMB >= this.compressionLevels[i].threshold) {
        return i
      }
    }
    return 0
  }

  static applyCompressionLevel(level, gameStore) {
    const compressionConfig = this.compressionLevels[level]

    compressionConfig.actions.forEach(action => {
      try {
        this.executeCompressionAction(action, gameStore)
      } catch (error) {
        console.error(`Failed to execute compression action ${action}:`, error)
      }
    })

    this.memoryStats.compressionLevel = level
    this.memoryStats.lastCleanup = Date.now()

    // Log results
    const newUsage = this.estimateMemoryUsage(gameStore)
    this.memoryStats.estimatedUsage = newUsage
  }

  static executeCompressionAction(action, gameStore) {
    switch (action) {
      case 'compressInactiveChocobos':
        ChocoboLazyLoader.compressInactiveChocobos(gameStore)
        break

      case 'trimTransactionHistory':
        if (gameStore.recentTransactions.length > 10) {
          gameStore.recentTransactions = gameStore.recentTransactions.slice(-10)
        }
        break

      case 'cleanupExpiredAuctions':
        gameStore.activeAuctions = gameStore.activeAuctions.filter(
          auction => auction.endDay >= gameStore.currentDay
        )
        break

      case 'compressOldRaceHistory':
        this.compressRaceHistories(gameStore, 10) // Keep only 10 most recent races
        break

      case 'retireOldChocobos':
        const retirements = RetirementManager.processNPCRetirements(gameStore)
        break

      case 'compressActiveChocobos':
        // More aggressive compression of even active chocobos
        gameStore.chocobos.forEach(chocobo => {
          if (chocobo.raceHistory && chocobo.raceHistory.length > 5) {
            chocobo.raceHistory = chocobo.raceHistory.slice(-5)
          }
        })
        break

      case 'clearCaches':
        ChocoboLazyLoader.clearCache()
        break

      default:
        console.warn(`Unknown compression action: ${action}`)
    }
  }

  static compressRaceHistories(gameStore, maxRaces) {
    gameStore.chocobos.forEach(chocobo => {
      if (chocobo.raceHistory && chocobo.raceHistory.length > maxRaces) {
        // Keep recent races and wins
        const recentRaces = chocobo.raceHistory.slice(-maxRaces)
        const wins = chocobo.raceHistory.filter(r => r.position === 1).slice(-3)
        
        // Combine, removing duplicates
        const raceIds = new Set(recentRaces.map(r => `${r.day}-${r.raceType}`))
        wins.forEach(win => {
          const id = `${win.day}-${win.raceType}`
          if (!raceIds.has(id)) {
            recentRaces.unshift(win)
          }
        })

        chocobo.raceHistory = recentRaces.slice(-maxRaces)
      }
    })
  }

  static scheduleMemoryCheck(gameStore) {
    // Schedule periodic memory checks
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        this.checkMemoryUsage(gameStore)
      })
    } else {
      // Fallback for environments without requestIdleCallback
      setTimeout(() => {
        this.checkMemoryUsage(gameStore)
      }, 100)
    }
  }

  static getMemoryReport(gameStore) {
    const stats = this.checkMemoryUsage(gameStore)
    
    return {
      estimatedUsage: stats.estimatedUsage,
      compressionLevel: stats.compressionLevel,
      compressionName: this.compressionLevels[stats.compressionLevel].name,
      chocobos: gameStore.chocobos.length,
      owners: gameStore.owners.length,
      transactions: gameStore.recentTransactions.length,
      auctions: gameStore.activeAuctions.length,
      cacheSize: ChocoboLazyLoader.detailCache.size,
      compressedChocobos: ChocoboLazyLoader.compressedChocobos.size,
      recommendations: this.getOptimizationRecommendations(gameStore)
    }
  }

  static getOptimizationRecommendations(gameStore) {
    const recommendations = []
    
    if (gameStore.chocobos.length > MEMORY_CONFIG.maxGlobalChocobos * 0.8) {
      recommendations.push('Consider retiring more chocobos to improve performance')
    }
    
    if (gameStore.recentTransactions.length > 15) {
      recommendations.push('Transaction history is growing large')
    }
    
    if (ChocoboLazyLoader.detailCache.size > 80) {
      recommendations.push('Many chocobos in detail cache - compression may help')
    }
    
    const avgRaceHistory = gameStore.chocobos.reduce((sum, c) => 
      sum + (c.raceHistory ? c.raceHistory.length : 0), 0
    ) / gameStore.chocobos.length
    
    if (avgRaceHistory > 15) {
      recommendations.push('Race histories are lengthy - consider archiving old races')
    }

    return recommendations
  }

  static forceCleanup(gameStore) {
    this.applyCompressionLevel(2, gameStore) // Apply moderate compression
  }

  static emergencyCleanup(gameStore) {
    this.applyCompressionLevel(3, gameStore) // Apply aggressive compression
    
    // Additional emergency measures
    if (gameStore.chocobos.length > MEMORY_CONFIG.maxGlobalChocobos) {
      const excess = gameStore.chocobos.length - MEMORY_CONFIG.maxGlobalChocobos + 50
      RetirementManager.checkGlobalPopulationCap(gameStore)
    }
  }
}